import React, {useState, useEffect, useRef} from 'react';
import {Search} from "lucide-react";
import {Department} from '../../../types/department';

// blueprint for the user
export interface User {
    id: number;
    name: string;
    department: Department;
    messengerId?: string;
}

export interface SearchBoxProps {
    onSelect: (user: User | null) => void;
    value: User | null; // Allows the parent component to control the selected value
}

export const Searchbox = ({onSelect, value}: SearchBoxProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [allUsers, setAllUsers] = useState<User[]>([]); // all employees from backend
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Fetch employees from backend on mount
    useEffect(() => {
        async function fetchEmployees() {
            try {
                const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
                const res = await fetch(`${API_BASE_URL}/api/Employees`);
                const data: User[] = await res.json();
                setAllUsers(data);
            } catch (err) {
                console.error("Failed to fetch employees:", err);
            }
        }

        fetchEmployees();
    }, []);

    // Syncs the search input's text with the parent's `value` prop.
    useEffect(() => {
        if (value) {
            setSearchTerm(value.name);
        } else {
            setSearchTerm('');
        }
    }, [value]);

    useEffect(() => {
        if (!isDropdownOpen){
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
                setFilteredUsers([]);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isDropdownOpen]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = event.target.value;
        setSearchTerm(newSearchTerm);
        setIsDropdownOpen(true);

        // If user starts typing, clear the parent's selection
        if (value) {
            onSelect(null);
        }

        if (newSearchTerm) {
            const result = allUsers.filter(user =>
                user.name.toLowerCase().includes(newSearchTerm.toLowerCase()) ||
                (user.department && user.department.toLowerCase().includes(newSearchTerm.toLowerCase()))
            );
            setFilteredUsers(result);
        } else {
            const sorted = [...allUsers].sort((a, b) => a.name.localeCompare(b.name));
            setFilteredUsers(sorted);
        }
    };

    const handleFocus = () => {
        setIsDropdownOpen(true);
        if (!searchTerm) {
            const sorted = [...allUsers].sort((a, b) => a.name.localeCompare(b.name));
            setFilteredUsers(sorted);
        }
    };

    const handleSelectUser = (user: User) => {
        onSelect(user); // Notify the parent of the selection.
        setFilteredUsers([]); // Close the suggestion list.
        setIsDropdownOpen(false);
    };

    return (
        <div className="relative w-full" ref={wrapperRef}>
            {/* Search Input Box */}
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="size-5 text-blue-500"/>
                </div>
                <input
                    type="text"
                    placeholder="Search by name or department..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={handleFocus}
                    className="w-full bg-gray-100 text-gray-800 text-sm md:text-base py-3 pl-10 pr-4 rounded-lg focus:outline-none transition-shadow hover:ring-2 hover:ring-blue-500"                />
            </div>

            {/* Search results list */}
            {isDropdownOpen && filteredUsers.length > 0 && (
                <ul className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredUsers.map(user => (
                        <li
                            key={user.id}
                            onClick={() => handleSelectUser(user)}
                            className="flex flex-col justify-center h-16 px-3 cursor-pointer border-b last:border-b-0 transition-colors hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-orange-400/10"
                        >
                            <p className="font-semibold text-gray-800">{user.name}</p>
                            <p className="text-sm text-gray-500">{user.department}</p>
                        </li>
                    ))}
                </ul>
            )}

            {/* Display selected user */}
            {value && filteredUsers.length === 0 && (
                <div
                    className="mt-4 p-4 border rounded-lg transition-shadow hover:shadow-lg hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-orange-400/10">
                    <p className="font-semibold text-gray-900">{value.name}</p>
                    <p className="text-sm text-gray-600">{value.department}</p>
                </div>
            )}
        </div>
    );
};
